/******************************************************************************
 *** BgtMbsdsExpndRsdocList ** 운영비 지출결의서 목록
*******************************************************************************/
import AppContext from "@/js/common/AppContext";
import AdminGridStyle from "@/js/common/ui/style/AdminGridStyle";
import GridTable, { fldInfsProps } from "@/js/common/ui/GridTable";
import React from "react";
import Axios from "@/js/common/Axios";
import Util from "@/js/common/Util";

interface Prpps {
    ognzCd            : string; // 조직코드
    acntgYear         : string; // 회계연도
    searchAtrzSttsCd  : string; // 결재진행상태(검색)
    setMbsdsExpndDocId: Function;
    prcsExpndRsdocSeCd: string; // 결의서 구분 검색 조건(일반, 무증빙[01] / 여입, 자료제출[]02)
    callTime: Date;
}

const BgtMbsdsExpndRsdocList = ( {ognzCd, acntgYear, searchAtrzSttsCd, setMbsdsExpndDocId, prcsExpndRsdocSeCd, callTime}: Prpps) => {
    // 목록 데이터
    const [dataArr   , setDataArr   ] = React.useState<any[]>([]);
    const [sumImplAmt, setSumImplAmt ] = React.useState<string>("0"); // 지출결의서 합계
    const [sumBankAmt, setSumBankAmt] = React.useState<string>("0"); // 통장잔액(결재완료 수입금액)
    const [sumDpstAmt, setSumDpstAmt] = React.useState<string>("0"); // 결재 후 잔액

    // GridTable 컬럼
    const fldInfs: fldInfsProps[] = [
        { type: "date" , fldNm: "implDt"         , width:  90, headAlign: "center", dataAlign: "center", label: "행위년월일" },
        { type: "text" , fldNm: "expndRsdocSeNm" , width:  60, headAlign: "center", dataAlign: "center", label: "구분"       },
        { type: "text" , fldNm: "bgtSbjct3stNm"  , width: 100, headAlign: "center", dataAlign: "center", label: "목"         },
        { type: "text" , fldNm: "abstrCn"        , width: 140, headAlign: "center", dataAlign: "left"  , label: "적요"       },
        { type: "comma", fldNm: "implAmt"        , width: 100, headAlign: "center", dataAlign: "right" , label: "금액"       },
        { type: "el"   , fldNm: "atrzSttsNm"     , width:  70, headAlign: "center", dataAlign: "center", label: "진행상태",
            el: (item: any) => {
                return (
                    <span className={"ls06 " + (item.atrzSttsCd === "02" ? "fc007bff" : "")}>{item.atrzSttsNm}</span> // 작성완료 Text 색상 변경
                )
            }
        },
        { type: "text" , fldNm: "crtDt"          , width: 100, headAlign: "center", dataAlign: "center", label: "작성일시"   },
        { type: "text" , fldNm: "expndRsdocNo"   , width: 130, headAlign: "center", dataAlign: "left"  , label: "문서번호"   },
    ];

    // 운영비 지출결의서 작성 목록 조회
    React.useEffect(() => {
        AppContext.showSpinner();
        Axios.dataAccess({
            url: "system/bgt/mbsds/expnd/select-bgt-mbsds-expnd-rsdoc-list.do",
            methodType: "post",
            paramType: "json",
            paramData: {
                orgCd             : ognzCd          ,  // 조직코드
                acntgYear         : acntgYear       ,  // 회계연도
                searchAtrzSttsCd  : searchAtrzSttsCd,  // 진행상태(검색)
                prcsExpndRsdocSeCd: prcsExpndRsdocSeCd // 결의서 구분(일반, 무증빙[01] / 여입, 자료제출[02])
            },
            onSuccessFn: (res: any) => {
                setDataArr(res.result.list);
                setSumImplAmt(res.result.listSum.sumImplAmt); // 지출결의서 합계
                setSumBankAmt (res.result.bnkbkSum.sumDpstAmt); // 통장잔액(결재완료 수입금액)
                const sumDpstAmt = (res.result.bnkbkSum.sumDpstAmt - res.result.listSum.sumImplAmt).toString();
                setSumDpstAmt (sumDpstAmt); // 결재 후 잔액 = 통장잔액 - 지출결의서
                AppContext.hideSpinner();
            },
        });
    }, [ognzCd, acntgYear, searchAtrzSttsCd, setDataArr, setSumDpstAmt, prcsExpndRsdocSeCd, callTime]);

    return (
        <div className="bd1d9d9d9 bdrd6 bgcWhite pt5 pl15 pr15 pb15">
            <div className="dpFlx aiC jcSb mb10 h44">
                <div className="fm-b fs16 fc007bff ls1 fw700">지출결의서 목록</div>
            </div>

            {/* 상단 요약 3개 박스 */}
            <div className="dpFlx gap14 bd1 bdcDDDDDD bdrd6 bgcFAFAFA p12 mb10">
                <div className="flx1 dpFlx flxdCol aiC">
                    <span className="fm-c fs12 fc666666 mb6">통장잔액</span>
                    <span className="fm-c fs16 fc333333">{Util.comma(sumBankAmt)}</span>
                </div>

                <div className="flx1 dpFlx flxdCol aiC">
                    <span className="fm-c fs12 fc666666 mb6">지출 결의서 합계</span>
                    <span className="fm-c fs16 fc007bff">{Util.comma(sumImplAmt)}</span>
                </div>

                <div className="flx1 dpFlx flxdCol aiC">
                    <span className="fm-c fs12 fc666666 mb6">결재 후 잔액</span>
                    <span className="fm-c fs16 fcFF0000">{Util.comma(sumDpstAmt)}</span>
                </div>
            </div>

            {/* 리스트 */}
            <div className="w100p ofxA ofyH bdt1 bdcEBEBEB pt6">
                <div className="mw900">
                    <GridTable
                        tableInfo={{
                            tableHeadVsb: true,
                            styleType: AdminGridStyle,
                            fldInfs: fldInfs,
                            ordFld: { name: "implDt", direction: "desc" },
                            dataArr: dataArr,
                            maxRowNums: [100],
                            theadTrHeight: 40,
                            tbodyTrHeight: 35,
                            paginateClass: "pt10",
                            paginateStyle: {
                                buttonOnColor: AppContext.boardThemeColor,
                                buttonWidth: 22,
                                buttonHeight: 24,
                                buttonMarginLeft: 1,
                                buttonMarginRight: 1,
                                fontSize: 13,
                                color: "#666666",
                                letterSpacing: -0.8,
                                fontFamily: "Pretendard-Regular",
                                borderRadius: 3,
                            },
                            // footerVsb: true,
                            rowClickFunc: (item: any) => {
                                // row click
                                setMbsdsExpndDocId(item.mbsdsExpndDocId);
                            },
                        }}
                    />
                </div>
                <table className="w100p bgcDCDCDC">
                    <colgroup>
                        <col style={{width: "90px" }}></col>
                        <col style={{width: "60px" }}></col>
                        <col style={{width: "100px"}}></col>
                        <col style={{width: "140px"}}></col>
                        <col style={{width: "100px"}}></col>
                        <col style={{width: "70px" }}></col>
                        <col style={{width: "100px"}}></col>
                        <col style={{width: "130px"}}></col>
                    </colgroup>
                    <tbody>
                        <tr style={{height: "35px"}}>
                            <td></td>
                            <td style={{textAlign: "center"}}><span className="fm-c fs15 ls06 fc2c3e50">합계</span></td>
                            <td colSpan={3} style={{textAlign: "right", paddingRight: "5px"}}>
                                <span className="fs15 ls075">{Util.comma(sumImplAmt)}</span>
                            </td>
                            <td></td>
                            <td></td>
                            <td></td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    )
}

export default BgtMbsdsExpndRsdocList;